import { useCurrency } from "@/app/providers/currencyContext";
import { ModifierRuleBadge } from "@/components/modifierRuleBadge/modifierRuleBadge";
import { QuantityStepper } from "@/components/quantityStepper/quantityStepper";
import { Modifier } from "@/dto/modifier";
import type { ModifierGroup } from "@/dto/modifierGroup";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import useMediaQuery from "@/utils/useMediaQuery";
import { useEffect } from "react";

export const ModalModifierSelection = ({
  selectableModifierGroups,
  selectedModifiers,
  setSelectedModifiers,
  handleModifierChange,
  handleModifierQuantityChange,
  merchantColor,
  isAddtocartClicked,
  isEssentials,
  isDisabled,
}: any) => {
  const isMobileScreen = useMediaQuery("(max-width: 768px)");
  useEffect(() => {
    if (isAddtocartClicked) {
      const firstUnmetGroup: ModifierGroup | undefined = Object.values(
        selectableModifierGroups || {}
      ).find((group: any) => {
        const groupSelections: { [key: string]: number } =
          selectedModifiers[group.id] || {};
        const totalGroupQuantity: number = Object.values(
          groupSelections
        ).reduce((acc: number, quantity: number) => acc + quantity, 0);
        return !(
          totalGroupQuantity >= group.minRequired &&
          totalGroupQuantity <= group.maxAllowed
        );
      }) as ModifierGroup | undefined;

      if (firstUnmetGroup) {
        const element = document.getElementById(firstUnmetGroup.id);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: isMobileScreen ? "start" : "nearest",
          });
        }
      }
    }
  }, [isAddtocartClicked, selectableModifierGroups, selectedModifiers]);

  return (
    <div
      className="flex flex-col"
      data-testid={applyDataTestId(pom.itemModal.modifierGroupDisplay)}
    >
      {selectableModifierGroups?.map((group: ModifierGroup) => (
        <ModifierGroup
          key={group.id}
          group={group}
          selectedModifiers={selectedModifiers}
          setSelectedModifiers={setSelectedModifiers}
          handleModifierChange={handleModifierChange}
          handleModifierQuantityChange={handleModifierQuantityChange}
          merchantColor={merchantColor}
          isAddtocartClicked={isAddtocartClicked}
          isEssentials={isEssentials}
          isDisabled={isDisabled}
        />
      ))}
    </div>
  );
};

const ModifierGroupHeader = ({
  group,
  isRequirementMet,
  isAddtocartClicked,
  isEssentials,
}: {
  group: ModifierGroup;
  isRequirementMet: boolean;
  isAddtocartClicked: boolean;
  isEssentials: boolean;
}) => {
  return (
    <div className="flex justify-between text-gray-500 mb-4 items-center">
      <div className="font-semibold text-[13px] text-[#727272]">
        {group.name.toUpperCase()}
      </div>
      {!isEssentials && (
        <ModifierRuleBadge
          min={group.minRequired}
          max={group.maxAllowed}
          isRequirementMet={isRequirementMet}
          isAddtocartClicked={isAddtocartClicked}
          modifierGroupId={group.id}
        />
      )}
    </div>
  );
};

const ModifierGroup = ({
  group,
  selectedModifiers,
  setSelectedModifiers,
  handleModifierChange,
  handleModifierQuantityChange,
  merchantColor,
  isAddtocartClicked,
  isEssentials,
  isDisabled,
}: any) => {
  const groupSelections: { [key: string]: number } =
    selectedModifiers[group.id] || {};
  const selectedCount: number = Object.values(groupSelections).reduce(
    (acc: number, quantity: number) => acc + quantity,
    0
  );
  const isRequirementMet: boolean =
    selectedCount >= group.minRequired && selectedCount <= group.maxAllowed;

  return (
    <div id={group.id} className="mb-4">
      <ModifierGroupHeader
        group={group}
        isRequirementMet={isRequirementMet}
        isAddtocartClicked={isAddtocartClicked}
        isEssentials={isEssentials}
      />
      <div>
        {group.modifiers.map((modifier: Modifier) => (
          <ModifierItem
            key={modifier.id}
            group={group}
            modifier={modifier}
            selectedModifiers={selectedModifiers}
            setSelectedModifiers={setSelectedModifiers}
            handleModifierChange={handleModifierChange}
            handleModifierQuantityChange={handleModifierQuantityChange}
            merchantColor={merchantColor}
            groupSelections={groupSelections}
            totalGroupQuantity={selectedCount}
            isEssentials={isEssentials}
            disabled={isDisabled}
          />
        ))}
      </div>
    </div>
  );
};

const ModifierItem = ({
  group,
  modifier,
  selectedModifiers,
  handleModifierChange,
  handleModifierQuantityChange,
  merchantColor,
  groupSelections,
  totalGroupQuantity,
  isEssentials,
  disabled,
}: any) => {
  const { format } = useCurrency();
  const isMaxReached: boolean = totalGroupQuantity >= group.maxAllowed;
  const isSelected: boolean = !!groupSelections[modifier.id];
  const isDisabled: boolean =
    disabled ||
    (!group.isSingleSelection && isMaxReached && !isSelected) ||
    !modifier.available;
  const modifierQuantity: number = groupSelections[modifier.id] || 1;
  const disablePlus: boolean =
    modifierQuantity >= Math.min(group.maxAllowed, 99) ||
    totalGroupQuantity >= group.maxAllowed;

  const selectedTextColor = isSelected ? "text-black" : "text-grayDark";
  const priceTextColor = isDisabled ? "text-grayDisabled" : selectedTextColor;

  return (
    <div
      key={modifier.id}
      className={`mb-4 border-b border-[#E5E5E5] pb-4 ${
        isDisabled ? "text-grayDisabled cursor-not-allowed" : ""
      }`}
    >
      <label
        className={`flex items-center justify-between ${
          isDisabled ? "cursor-default" : "cursor-pointer"
        }`}
      >
        <div className="flex items-center">
          {group.isSingleSelection ? (
            <ModifierRadioSelection
              group={group}
              modifier={modifier}
              selectedModifiers={selectedModifiers}
              handleModifierChange={handleModifierChange}
              merchantColor={merchantColor}
              isDisabled={isDisabled}
            />
          ) : (
            <ModifierCheckboxSelection
              group={group}
              modifier={modifier}
              selectedModifiers={selectedModifiers}
              handleModifierChange={handleModifierChange}
              isSingleSelection={group.isSingleSelection}
              isDisabled={isDisabled}
              merchantColor={merchantColor}
            />
          )}
          <div
            className={`${
              isDisabled
                ? "text-grayDisabled cursor-not-allowed"
                : "text-black cursor-pointer"
            }`}
          >
            {modifier.name}
          </div>
        </div>
        <span
          data-testid={
            applyDataTestId(pom.itemPage.modifierPrice) + modifier.id
          }
          className={`text-right font-normal text-[15px] ${priceTextColor}`}
        >
          +{format(modifier.price)}
        </span>
      </label>
      {!group.isSingleSelection && !isEssentials && isSelected && (
        <span className={`pl-7 mt-2 block`}>
          <QuantityStepper
            handleQuantityChange={(newQuantity: number) =>
              handleModifierQuantityChange(group.id, modifier.id, newQuantity)
            }
            quantity={modifierQuantity}
            max={group.maxAllowed}
            disablePlus={disablePlus}
            testId={applyDataTestId(pom.steppers.quantityStepper) + modifier.id}
            modifierId={modifier.id}
            totalQuantity={totalGroupQuantity}
            adaName={modifier?.name}
          />
        </span>
      )}
    </div>
  );
};

const ModifierRadioSelection = ({
  group,
  modifier,
  selectedModifiers,
  handleModifierChange,
  merchantColor,
  isDisabled,
}: any) => {
  const { format } = useCurrency();
  const isSelected: boolean = !!selectedModifiers[group.id]?.[modifier.id];

  return (
    <input
      type="radio"
      name={group.id}
      value={modifier.id}
      checked={isSelected && !isDisabled}
      disabled={isDisabled}
      onChange={() => handleModifierChange(group.id, modifier.id, true)}
      className="mr-2"
      style={{
        width: "20px",
        height: "20px",
        cursor: "pointer",
        accentColor: merchantColor,
        flexShrink: 0,
      }}
      data-testid={applyDataTestId(pom.itemPage.modifierInput) + modifier.id}
      aria-label={`${modifier.name} ${format(modifier.price)}`}
    />
  );
};

const ModifierCheckboxSelection = ({
  group,
  modifier,
  selectedModifiers,
  handleModifierChange,
  isSingleSelection,
  isDisabled,
  merchantColor,
}: any) => {
  const { format } = useCurrency();
  const isChecked: boolean = !!selectedModifiers[group.id]?.[modifier.id];
  return (
    <input
      type="checkbox"
      name={group.id}
      value={modifier.id}
      checked={isChecked && !isDisabled}
      onChange={() =>
        handleModifierChange(group.id, modifier.id, isSingleSelection)
      }
      disabled={isDisabled}
      className="mr-2"
      style={{
        width: "20px",
        height: "20px",
        cursor: isDisabled ? "not-allowed" : "pointer",
        accentColor: merchantColor,
        flexShrink: 0,
      }}
      data-testid={applyDataTestId(pom.itemPage.modifierInput) + modifier.id}
      aria-label={`${modifier.name} ${format(modifier.price)}`}
    />
  );
};
